// src/components/UserContractView/UserContractView.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent,
  Chip, Grid, CircularProgress, Badge,
  IconButton, useMediaQuery, Stack, Container, Avatar, Tabs, Tab
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import {
  Assignment as ContractIcon,
  Folder as FolderIcon,
  CheckCircle as SignedIcon,
  Pending as PendingIcon,
  Cancel as DeclinedIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Description as TemplateIcon
} from '@mui/icons-material';

import client from '../../API';
import EnhancedContractTemplateDialog from '../Dashboard/Company/userCompanyTools/contractbuilder';

// Import modular components
import StatsCard from './statsCards';
import UserAssignmentDialog from './userAssignmentDialog';
import PendingContractsSection from './PendingContractsSection';
import TemplatesSection from './TemplateSelection';
import HistorySection from './HistorySelection';
import EnhancedSignDialog from './EnhancedSignDialog';
import MyAssignmentsSection from './MyAssignments';

// Import constants and utilities
import { CONTRACT_STATUS } from './contractConstants';
import { getAssignmentId } from './contractUtils';

// ------- Main Component -------
const UserContractView = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Data state
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [activeSignature, setActiveSignature] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  // Template management state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignTemplate, setAssignTemplate] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [p, h, t] = await Promise.all([
        client.contracts.getMyPendingContracts(),
        client.contracts.getMySignatures(),
        client.contracts.user.getMyTemplates(),
      ]);
      setPending(p?.pending_contracts || []);
      setHistory(h?.signatures || []);
      setUserTemplates(t?.user_templates || []);
      setPublicTemplates(t?.public_templates || []);
    } catch (e) {
      console.error('Failed to fetch contract data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation
  const navigateToContract = (assignmentId) => {
    if (!assignmentId) {
      alert('Invalid assignment ID');
      return;
    }
    navigate(`/contract/${assignmentId}`);
  };

  // Template Actions
  const handleCreateTemplate = () => {
    setEditTemplate(null);
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditTemplate(template);
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    await fetchData();
    setTemplateDialogOpen(false);
    setEditTemplate(null);
  };

  const handleCloneTemplate = async (template) => {
    const newName = prompt(`Clone template: ${template.name}`, `${template.name} (Copy)`);
    if (!newName) return;

    try {
      await client.contracts.user.cloneTemplate(template.id, { name: newName });
      await fetchData();
      alert('Template cloned successfully!');
    } catch (error) {
      console.error('Failed to clone template:', error);
      alert(error.response?.data?.error || 'Failed to clone template');
    }
  };

  const handleShareTemplate = async (template) => {
    const action = template.is_public ? 'make private' : 'make public';
    if (!confirm(`Are you sure you want to ${action} this template?`)) return;

    try {
      await client.contracts.user.shareTemplate(template.id);
      await fetchData();
      alert(`Template is now ${template.is_public ? 'private' : 'public'}`);
    } catch (error) {
      console.error('Failed to toggle template visibility:', error);
      alert(error.response?.data?.error || 'Failed to update template');
    }
  };

  const handleAssignTemplate = (template) => {
    setAssignTemplate(template);
    setAssignmentDialogOpen(true);
  };

  const handleAssignSubmit = async (assignmentData) => {
    try {
      // Check if we have email invites (external recipients)
      const hasEmailInvites = assignmentData.email_invites && assignmentData.email_invites.length > 0;
      const hasExistingUsers = assignmentData.user_ids && assignmentData.user_ids.length > 0;
      
      if (hasEmailInvites && !hasExistingUsers) {
        // All recipients are external - use the external assignment endpoint
        const response = await client.contracts.user.assignTemplateToExternalUsers({
          template_id: assignmentData.template_id,
          recipients: assignmentData.email_invites,
          message: assignmentData.assignment_message,
          due_date: assignmentData.due_date,
          send_notifications: true
        });
        
        await fetchData();
        alert(`Contract assigned successfully to ${response.results.created} external recipient(s)!`);
        
        if (response.results.failed > 0) {
          console.warn('Some recipients failed:', response.failed_recipients);
          alert(`Warning: ${response.results.failed} recipient(s) failed. Check console for details.`);
        }
        
      } else if (hasEmailInvites && hasExistingUsers) {
        // Mixed: both existing users and external invites
        // First assign to existing users
        await client.contracts.user.assignTemplate({
          ...assignmentData,
          scope: 'specific_users',
          user_ids: assignmentData.user_ids,
          email_invites: [] // Clear email invites for this call
        });
        
        // Then create external signatures for email invites
        const externalResponse = await client.contracts.user.assignTemplateToExternalUsers({
          template_id: assignmentData.template_id,
          recipients: assignmentData.email_invites,
          message: assignmentData.assignment_message,
          due_date: assignmentData.due_date,
          send_notifications: true
        });
        
        await fetchData();
        alert(
          `Contract assigned successfully!\n` +
          `- ${assignmentData.user_ids.length} existing user(s)\n` +
          `- ${externalResponse.results.created} external recipient(s)`
        );
        
      } else {
        // Only existing users - use normal assignment
        await client.contracts.user.assignTemplate(assignmentData);
        await fetchData();
        alert('Contract assigned successfully!');
      }
      
      setAssignmentDialogOpen(false);
      setAssignTemplate(null);
      
    } catch (error) {
      console.error('Failed to assign template:', error);
      alert(error.response?.data?.error || 'Failed to assign contract');
      throw error;
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await client.contracts.user.deleteTemplate(template.id);
      await fetchData();
      alert('Template deleted successfully');
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(error.response?.data?.error || 'Failed to delete template');
    }
  };

  // Contract Actions
  const updateSignature = (updated) => {
    const assignmentId = getAssignmentId(updated);
    
    setPending((prev) =>
      prev.filter((item) => getAssignmentId(item) !== assignmentId)
    );
    
    setHistory((prev) => {
      const idx = prev.findIndex((sig) => getAssignmentId(sig) === assignmentId);
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], ...updated };
        return clone;
      }
      return [...prev, updated];
    });
  };

  const handleAgreeOrSign = async (signature, isSimple) => {
    const sigId = signature?.id;
    try {
      setActionLoading(true);
      const res = isSimple
        ? await client.contracts.agreeToContract(sigId)
        : await client.contracts.signContract(sigId);
      updateSignature(res.signature || res.data?.signature || signature);
      closeDialog();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to complete action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async (signature, reason) => {
    const sigId = signature?.id;
    try {
      setActionLoading(true);
      const res = await client.contracts.declineContract(sigId, reason);
      updateSignature(res.signature || res.data?.signature || signature);
      closeDialog();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to decline');
    } finally {
      setActionLoading(false);
    }
  };

  const loadContractFromAssignment = async (assignmentId) => {
    const data = await client.contracts.getContractFromAssignment(assignmentId);
    return data;
  };

  const openDialog = async (item, readonly = false) => {
    const assignmentId = getAssignmentId(item);
    const assignment = item?.template ? item : item?.assignment;
    const signature = item?.template ? null : item;

    if (!assignmentId) {
      alert('Invalid assignment ID - cannot load contract');
      return;
    }

    setActiveAssignment(assignment);
    setActiveSignature(signature);
    setReadOnly(readonly);
    setDialogOpen(true);
    setContractLoading(true);
    setContractData(null);

    try {
      const fullContractData = await loadContractFromAssignment(assignmentId);
      setContractData(fullContractData);
    } catch (error) {
      console.error('Failed to load contract data:', error);
      alert('Failed to load contract details');
      closeDialog();
    } finally {
      setContractLoading(false);
    }
  };

  const closeDialog = () => {
    setActiveAssignment(null);
    setActiveSignature(null);
    setContractData(null);
    setReadOnly(false);
    setDialogOpen(false);
    setContractLoading(false);
  };

  // Calculate stats
  const signedCount = history.filter(
    s => s.status === CONTRACT_STATUS.SIGNED || s.status === CONTRACT_STATUS.AGREED
  ).length;
  const declinedCount = history.filter(s => s.status === CONTRACT_STATUS.DECLINED).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <ContractIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Contract Center
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your agreements, templates, and signatures
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pending"
            value={pending.length}
            icon={<PendingIcon />}
            color="warning"
            subtitle="Awaiting action"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Templates"
            value={userTemplates.length}
            icon={<FolderIcon />}
            color="primary"
            subtitle="Ready to use"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Completed"
            value={signedCount}
            icon={<SignedIcon />}
            color="success"
            subtitle="All time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Declined"
            value={declinedCount}
            icon={<DeclinedIcon />}
            color="error"
            subtitle="Total declined"
          />
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<PendingIcon />} 
            iconPosition="start" 
            label="Pending Contracts" 
          />
          <Tab 
            icon={<TemplateIcon />} 
            iconPosition="start" 
            label="Templates" 
          />
          <Tab 
            icon={<SendIcon />} 
            iconPosition="start" 
            label="My Assignments" 
          />
          <Tab 
            icon={<HistoryIcon />} 
            iconPosition="start" 
            label="History" 
          />
        </Tabs>
      </Card>

      {/* Tab Panels */}
      <Box>
        {/* Pending Contracts Tab */}
        {activeTab === 0 && (
          <PendingContractsSection
            pending={pending}
            onOpenContract={navigateToContract}
            onQuickPreview={openDialog}
            loading={false}
          />
        )}

        {/* Templates Tab */}
        {activeTab === 1 && (
          <TemplatesSection
            userTemplates={userTemplates}
            publicTemplates={publicTemplates}
            onCreateTemplate={handleCreateTemplate}
            onEditTemplate={handleEditTemplate}
            onCloneTemplate={handleCloneTemplate}
            onShareTemplate={handleShareTemplate}
            onAssignTemplate={handleAssignTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            loading={false}
          />
        )}

        {/* My Assignments Tab */}
        {activeTab === 2 && (
          <MyAssignmentsSection />
        )}

        {/* History Tab */}
        {activeTab === 3 && (
          <HistorySection
            history={history}
            onOpenContract={navigateToContract}
            onQuickPreview={openDialog}
            loading={false}
          />
        )}
      </Box>

      {/* Contract Preview Dialog */}
      <EnhancedSignDialog
        open={dialogOpen}
        assignment={activeAssignment}
        signature={activeSignature}
        contractData={contractData}
        loading={contractLoading}
        onClose={closeDialog}
        onAgree={handleAgreeOrSign}
        onDecline={handleDecline}
        actionLoading={actionLoading}
        readOnly={readOnly || activeSignature?.status !== CONTRACT_STATUS.PENDING}
        onNavigateToContract={() => {
          const assignmentId = getAssignmentId(activeAssignment || activeSignature);
          if (assignmentId) {
            closeDialog();
            navigateToContract(assignmentId);
          }
        }}
      />

      {/* Template Builder Dialog */}
      <EnhancedContractTemplateDialog
        open={templateDialogOpen}
        template={editTemplate}
        onClose={() => {
          setTemplateDialogOpen(false);
          setEditTemplate(null);
        }}
        onSave={handleSaveTemplate}
        userMode={true}
      />

      {/* Assignment Dialog */}
      <UserAssignmentDialog
        open={assignmentDialogOpen}
        template={assignTemplate}
        onClose={() => {
          setAssignmentDialogOpen(false);
          setAssignTemplate(null);
        }}
        onAssign={handleAssignSubmit}
      />
    </Container>
  );
};

// Contract Widget for Dashboard
export const ContractWidget = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { pending_contracts = [] } = await client.contracts.getMyPendingContracts();
      setCount(pending_contracts.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Card>
    );
  }

  if (count === 0) return null;

  return (
    <Card sx={{ p: 2, bgcolor: '#fff3cd', borderColor: '#ffeaa7', border: '1px solid' }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Badge badgeContent={count} color="warning">
          <ContractIcon color="warning" />
        </Badge>
        <Box flex={1}>
          <Typography variant="h6" color="warning.dark">Pending Contracts</Typography>
          <Typography variant="body2">You have {count} contract{count > 1 && 's'} waiting</Typography>
        </Box>
        <Button variant="outlined" size="small" href="/contracts">
          Review
        </Button>
      </Box>
    </Card>
  );
};

export default UserContractView;